import React, { useMemo } from 'react';
import { useTranslation } from '@/store/languageStore';
import type { GanttData, ProjectTask, ProjectMilestone } from '@/types/projects';
import { getPublicHolidayDatesForYear } from '@/utils/swedishHolidays';

interface Props {
  data: GanttData;
  onTaskClick?: (task: ProjectTask) => void;
}

const DAY_WIDTH = 28;
const ROW_HEIGHT = 36;

function parseDate(s?: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function daysBetween(a: Date, b: Date) {
  return Math.ceil((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Röd om uppgiften passerat planerat slut utan att vara klar, eller avslutats efter plan. */
function isTaskOverdue(task: ProjectTask): boolean {
  const plannedEnd = parseDate(task.planned_end);
  if (!plannedEnd) return false;

  const deadline = startOfDay(plannedEnd);
  const today = startOfDay(new Date());

  if (task.status === 'done' || task.status === 'cancelled') {
    const actualEnd = parseDate(task.actual_end);
    return actualEnd ? startOfDay(actualEnd) > deadline : false;
  }

  return today > deadline;
}

export const ProjectGantt: React.FC<Props> = ({ data, onTaskClick }) => {
  const { t } = useTranslation();

  const { startDate, totalDays, holidays } = useMemo(() => {
    const dates: Date[] = [];
    for (const task of data.tasks) {
      const s = parseDate(task.planned_start);
      const e = parseDate(task.planned_end);
      if (s) dates.push(s);
      if (e) dates.push(e);
    }
    for (const ms of data.milestones) {
      const d = parseDate(ms.due_date || ms.target_date);
      if (d) dates.push(d);
    }
    if (!dates.length) {
      const today = new Date();
      return { startDate: today, totalDays: 30, holidays: new Set<string>() };
    }
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    min.setDate(min.getDate() - 3);
    max.setDate(max.getDate() + 7);
    const years = new Set([min.getFullYear(), max.getFullYear()]);
    const hol = new Set<string>();
    years.forEach((y) => getPublicHolidayDatesForYear(y).forEach((d) => hol.add(d)));
    return { startDate: min, totalDays: Math.max(daysBetween(min, max), 14), holidays: hol };
  }, [data]);

  const dayHeaders = useMemo(() => {
    return Array.from({ length: totalDays }, (_, i) => {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      const dow = d.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const isHoliday = holidays.has(key);
      return { d, key, isWeekend, isHoliday };
    });
  }, [startDate, totalDays, holidays]);

  const barLeft = (date: Date) => daysBetween(startDate, date) * DAY_WIDTH;
  const barWidth = (start: Date, end: Date) => Math.max(daysBetween(start, end), 1) * DAY_WIDTH;

  const criticalTaskIds = useMemo(() => {
    const deps = data.dependencies.filter((d) => d.dependency_type === 'finish_to_start');
    const ends = new Map<number, Date>();
    for (const task of data.tasks) {
      const e = parseDate(task.planned_end);
      if (e) ends.set(task.id, e);
    }
    let latest = 0;
    let latestDate: Date | null = null;
    ends.forEach((date, id) => {
      if (!latestDate || date > latestDate) {
        latestDate = date;
        latest = id;
      }
    });
    const chain = new Set<number>();
    if (latest) {
      chain.add(latest);
      let current = latest;
      for (let i = 0; i < 20; i++) {
        const dep = deps.find((d) => d.task_id === current);
        if (!dep) break;
        chain.add(dep.depends_on_task_id);
        current = dep.depends_on_task_id;
      }
    }
    return chain;
  }, [data]);

  const renderBar = (task: ProjectTask, taskIndex: number) => {
    const s = parseDate(task.planned_start);
    const e = parseDate(task.planned_end);
    if (!s || !e) return null;
    const overdue = isTaskOverdue(task);
    return (
      <div
        key={task.id}
        className={`absolute h-5 rounded cursor-pointer text-[10px] text-white flex items-center px-1 truncate ${
          overdue ? 'bg-red-500' : 'bg-green-500'
        }`}
        style={{
          top: 16 + taskIndex * 22,
          left: barLeft(s),
          width: barWidth(s, e),
        }}
        title={task.title}
        onClick={() => onTaskClick?.(task)}
      >
        {task.title}
      </div>
    );
  };

  const renderMilestone = (ms: ProjectMilestone) => {
    const d = parseDate(ms.due_date || ms.target_date);
    if (!d) return null;
    return (
      <div
        key={`ms-${ms.id}`}
        className="absolute w-3 h-3 bg-amber-500 rotate-45 z-10"
        style={{ top: 6, left: barLeft(d) }}
        title={ms.name}
      />
    );
  };

  const milestoneIds = new Set(data.milestones.map((m) => m.id));
  const unassignedTasks = data.tasks.filter(
    (t) => !t.milestone_id || !milestoneIds.has(t.milestone_id)
  );

  const rows: { label: string; milestone?: ProjectMilestone; tasks: ProjectTask[] }[] = [
    ...data.milestones.map((m) => ({
      label: m.name,
      milestone: m,
      tasks: data.tasks.filter((t) => t.milestone_id === m.id),
    })),
  ];
  if (unassignedTasks.length) {
    rows.push({ label: t('projects.gantt.unassignedTasks'), tasks: unassignedTasks });
  }

  const rowHeightFor = (tasks: ProjectTask[]) => {
    const withDates = tasks.filter((t) => parseDate(t.planned_start) && parseDate(t.planned_end));
    if (!withDates.length) return ROW_HEIGHT;
    return Math.max(ROW_HEIGHT, 16 + withDates.length * 22 + 4);
  };

  const chartWidth = totalDays * DAY_WIDTH;

  return (
    <div className="border border-border rounded-lg overflow-auto bg-bgBase">
      <div className="flex border-b border-border sticky top-0 bg-bgElevated z-10">
        <div className="w-48 flex-shrink-0 p-2 text-xs font-medium text-textSecondary border-r border-border">
          {t('projects.gantt.task')}
        </div>
        <div className="flex" style={{ width: chartWidth }}>
          {dayHeaders.map(({ d, key, isWeekend, isHoliday }) => (
            <div
              key={key}
              className={`flex-shrink-0 text-center text-[10px] py-1 border-r border-border/50 ${
                isHoliday ? 'bg-red-100 dark:bg-red-900/20' : isWeekend ? 'bg-bgElevated' : ''
              }`}
              style={{ width: DAY_WIDTH }}
              title={isHoliday ? t('projects.gantt.holiday') : undefined}
            >
              {d.getDate()}
            </div>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="p-4 text-textSecondary text-sm">{t('projects.gantt.noData')}</p>
      ) : (
        rows.map((row, i) => {
          const h = rowHeightFor(row.tasks);
          return (
          <div key={i} className="flex border-b border-border/50" style={{ height: h }}>
            <div className="w-48 flex-shrink-0 px-2 py-1 text-xs text-textDefault truncate border-r border-border">
              {row.milestone ? `◆ ${row.label}` : row.label}
              {row.tasks.length > 0 && (
                <span className="text-textSecondary ml-1">({row.tasks.length})</span>
              )}
            </div>
            <div className="relative" style={{ width: chartWidth, height: h }}>
              {dayHeaders.map(({ key, isWeekend, isHoliday }, di) => (
                <div
                  key={key}
                  className={`absolute top-0 bottom-0 border-r border-border/30 ${
                    isHoliday ? 'bg-red-50 dark:bg-red-900/10' : isWeekend ? 'bg-bgElevated/50' : ''
                  }`}
                  style={{ left: di * DAY_WIDTH, width: DAY_WIDTH }}
                />
              ))}
              {row.milestone && renderMilestone(row.milestone)}
              {row.tasks.map((task, ti) => renderBar(task, ti))}
            </div>
          </div>
          );
        })
      )}
      {data.dependencies.length > 0 && (
        <p className="p-2 text-xs text-textSecondary">
          {t('projects.gantt.dependencies')}: {data.dependencies.length}
          {' · '}
          {t('projects.gantt.criticalPath')}: {criticalTaskIds.size} {t('projects.gantt.tasks')}
        </p>
      )}
    </div>
  );
};
